import axios from "axios";
import { push } from "react-router-redux";
import qs from "qs";

import * as authenticationActions from "./actions";
import { CHANGE_PWD_PATH } from "../../app/AppRoutes";
import { State } from "../state";
import { User, API_ERROR_CODE, DEFAULT_ERROR_MESSAGE } from "../../types";
import { BASE_URL } from "../../app/appConstants";
import { AppDispatch } from "../configureStore";
import keycloak from "../../app/authentication/keycloak/keycloak";
import {
  removeSessionCookies,
  setSessionCookies,
  getCookie,
  getUserFromCookie,
} from "../../app/authentication/keycloak/KeycloakProvider";

export type ValueOf<T> = T[keyof T];

export const requestToken = (user: User) => (dispatch: AppDispatch) => {
  const basicAuth = window.btoa("web:topsecretweb");
  removeSessionCookies(true);

  axios
    .post(
      `${BASE_URL}/oauth/token`,
      qs.stringify({
        grant_type: "password",
        username: user.username,
        password: user.password,
      }),
      {
        headers: {
          Authorization: "Basic " + basicAuth,
          accept: "application/json",
          "Content-Type": "application/x-www-form-urlencoded",
        },
      }
    )
    .then((response) => {
      const accessToken = response.data.access_token;
      const refreshToken = response.data.refresh_token;
      dispatch(
        authenticationActions.setTokens({
          accessToken,
          refreshToken,
        })
      );

      setSessionCookies({ accessToken, refreshToken });

      axios
        .post(`${BASE_URL}/mps/web/login`, null, {
          headers: {
            Authorization: "Bearer " + response.data.access_token,
            accept: "application/json",
          },
        })
        .then((response) => {
          if (user) {
            user.role = response.data.userRole;
            user.customerId = response.data.customerId;
            user.fullname = response.data.fullName;
          }
          setSessionCookies({ user });
          return dispatch(authenticationActions.loginSuccessful(user));
        })
        .catch((error) => {
          const { response } = error;
          return Promise.reject(response);
        });
    })
    .catch((error) => {
      const { response } = error;
      if (response) {
        if (
          response &&
          response.status === 400 &&
          response.data.error_description === "User credentials have expired"
        ) {
          dispatch(authenticationActions.setUsername(user));
          dispatch(authenticationActions.setInitiatePasswordChange());
          return dispatch(push(CHANGE_PWD_PATH));
        } else {
          dispatch(
            authenticationActions.loginFailed(
              "There was an issue logging in. Please retry."
            )
          );
        }
      }
      return Promise.reject(response);
    });
};

export const checkAuthStatus =
  () => (dispatch: AppDispatch, getState: () => State) => {
    if (getState().authentication.accessToken) {
      return true;
    } else {
      const accessToken = getCookie("access_token");
      const refreshToken = getCookie("refresh_token");
      const user = getUserFromCookie();

      if (accessToken) {
        const decodedToken = parseJwt(accessToken);

        if (decodedToken) {
          const issuer = decodedToken.iss;
          console.log("Issuer:", issuer);

          if (issuer === process.env.REACT_APP_KEYCLOAK_ISSUER) {
            dispatch(authenticationActions.setAuthenticatedWithKeycloak(true));
          }
        }
      }

      if (user) {
        dispatch(authenticationActions.setUsername(user));
      }
      if (accessToken && refreshToken) {
        dispatch(
          authenticationActions.setTokens({
            accessToken,
            refreshToken,
          })
        );
      }
    }
    return false;
  };

export const parseJwt = (token: string): { [key: string]: any } | null => {
  try {
    const base64Url = token.split(".")[1];
    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split("")
        .map(function (c) {
          return "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2);
        })
        .join("")
    );

    return JSON.parse(jsonPayload);
  } catch (e) {
    console.error("Invalid JWT token", e);
    return null;
  }
};

export const requestLogout =
  (): any => (dispatch: AppDispatch, getState: () => State) => {
    const authenticatedWithKeycloak =
      getState().authentication.authenticatedWithKeycloak;

    if (authenticatedWithKeycloak) {
      dispatch(requestLogoutKeycloak());
    } else {
      dispatch(requestLogoutLegacy());
    }
  };

const requestLogoutKeycloak =
  (): any => async (dispatch: AppDispatch, getState: () => State) => {
    try {
      const logoutOptions = {
        redirectUri: window.location.origin,
      };

      await keycloak.logout(logoutOptions);
      removeSessionCookies(true);
      dispatch(authenticationActions.logoutSuccessful());
    } catch (error) {
      console.error("Failed to logout from Keycloak", error);
    }
  };

const requestLogoutLegacy =
  (): any => (dispatch: AppDispatch, getState: () => State) => {
    console.log("legacy logout");

    const accessToken = getState().authentication.accessToken;
    if (accessToken) {
      axios.post(`${BASE_URL}/oauth/revoke`, null, {
        headers: {
          Authorization: "Bearer " + accessToken,
          accept: "application/json",
        },
      });
    }

    removeSessionCookies(true);

    return dispatch(authenticationActions.logoutSuccessful());
  };

export const requestSignUp = (email: string) => (dispatch: AppDispatch) => {
  dispatch(authenticationActions.startSpinner());
  return axios
    .post(
      `${BASE_URL}/mps/web/signup`,
      {
        email: email,
      },
      {
        headers: {
          accept: "application/json",
          Authorization: "",
        },
      }
    )
    .then(() => dispatch(authenticationActions.stopSpinner()))
    .catch(() => {
      dispatch(
        authenticationActions.setFailureMessage(
          "There was a server issue. Please retry or submit a Support Desk ticket."
        )
      );
      dispatch(authenticationActions.stopSpinner());
      return Promise.reject();
    });
};

export const requestForgotPassword =
  (username: string) => (dispatch: AppDispatch) => {
    dispatch(authenticationActions.startSpinner());
    return axios
      .post(
        `${BASE_URL}/mps/web/forgot-password`,
        {
          username: username,
          urlTypeIdentifier: document.location.protocol.replace(":", ""),
        },
        {
          headers: {
            accept: "application/json",
            Authorization: "",
          },
        }
      )
      .then(() => dispatch(authenticationActions.stopSpinner()))
      .catch(() => {
        dispatch(
          authenticationActions.setFailureMessage(
            "There was a server issue. Please retry or submit a Support Desk ticket."
          )
        );
        dispatch(authenticationActions.stopSpinner());
        return Promise.reject();
      });
  };

export const requestConfirmResetPassword =
  (token: string) => (dispatch: AppDispatch) => {
    return axios
      .post(`${BASE_URL}/mps/web/confirm-reset`, null, {
        params: {
          token: token,
        },
        headers: {
          accept: "application/json",
          Authorization: "",
        },
      })
      .then((response) => {
        const user = {
          username: response.data.username,
          password: "",
          role: "",
          customerId: 0,
          fullname: "",
        };
        dispatch(authenticationActions.setUsername(user));
        return Promise.resolve();
      })
      .catch((error) => {
        const { response } = error;
        return Promise.reject(response);
      });
  };

export const requestResetPassword =
  (username: string, newPassword: string, token: string) =>
  (dispatch: AppDispatch) => {
    return axios
      .post(
        `${BASE_URL}/mps/web/reset-password`,
        {
          username: username,
          newPassword: newPassword,
          token: token,
        },
        {
          headers: {
            accept: "application/json",
            Authorization: "",
          },
        }
      )
      .catch((error) => {
        if (!error.response) {
          dispatch(
            authenticationActions.setFailureMessage(DEFAULT_ERROR_MESSAGE)
          );
          return Promise.reject();
        }

        const errorCode: keyof typeof API_ERROR_CODE =
          error.response.data.error.errorCode;
        const errorMessage = API_ERROR_CODE[errorCode] || DEFAULT_ERROR_MESSAGE;
        dispatch(authenticationActions.setFailureMessage(errorMessage));
        return Promise.reject();
      });
  };

export const requestChangePassword =
  (username: string, oldPassword: string, newPassword: string) =>
  (dispatch: AppDispatch, getState: () => State) => {
    return axios
      .post(
        `${BASE_URL}/mps/web/change-password`,
        {
          username: username,
          oldPassword: oldPassword,
          newPassword: newPassword,
        },
        {
          headers: {
            accept: "application/json",
            Authorization: "",
          },
        }
      )
      .catch((error) => {
        if (!error.response) {
          dispatch(
            authenticationActions.setFailureMessage(DEFAULT_ERROR_MESSAGE)
          );
          return Promise.reject();
        }

        const errorCode: keyof typeof API_ERROR_CODE =
          error.response.data.error.errorCode;
        const errorMessage = API_ERROR_CODE[errorCode] || DEFAULT_ERROR_MESSAGE;

        dispatch(authenticationActions.setFailureMessage(errorMessage));
        return Promise.reject();
      });
  };
